import { extractSubdomain } from '@/auth';
import { client } from '@/client';

const SCANNET_HOST = 'app.eu.cyberpilot.io';

// Host suffixes that should be checked for a Scannet redirect, mapped to the
// target host suffix to redirect to.
const REDIRECT_SOURCE_HOST_SUFFIXES: Record<string, string> = {
  'app.cyberpilot.io': SCANNET_HOST,
  // Local dev has no local Scannet counterpart — redirect to production EU so
  // the flag can be tested manually. Remove once the AWS -> Scannet migration
  // is complete.
  'app.cyber-pilot.local': SCANNET_HOST,
};

const matchRedirectSourceHostSuffix = (): string | undefined => {
  const { hostname } = window.location;
  return Object.keys(REDIRECT_SOURCE_HOST_SUFFIXES).find(
    (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`),
  );
};

// Resolves true if a redirect was issued — caller should not render the app in that case.
// Never rejects: any unexpected error fails open (returns false) so the app always renders.
export const redirectToScannetIfAccountMoved = async (): Promise<boolean> => {
  try {
    if (process.env?.REACT_APP_FORCE_CONFIG_URLS === 'true') {
      return false;
    }

    const matchedSuffix = matchRedirectSourceHostSuffix();
    if (!matchedSuffix) return false;

    const subdomain = extractSubdomain(window.location);
    if (!subdomain || subdomain === 'app' || subdomain === 'awareness') {
      return false;
    }

    const { shouldRedirectToScannet } = await client.accounts.getSubdomain(
      subdomain,
    );
    if (!shouldRedirectToScannet) return false;

    const targetSuffix = REDIRECT_SOURCE_HOST_SUFFIXES[matchedSuffix];
    const newHostname = window.location.hostname.replace(
      matchedSuffix,
      targetSuffix,
    );
    // No port: the redirect target is always a real host on the standard port,
    // even when the source is a local dev host with a non-standard port.
    window.location.replace(
      `${window.location.protocol}//${newHostname}${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
    return true;
  } catch {
    // Unknown subdomain / network error / unexpected failure — fail open, load the app normally.
    return false;
  }
};
